import {
  Alert,
  BodyText,
  Checkbox,
  Flex,
  PasswordInput,
  RadioGroup,
  RadioTile,
  Select,
  SelectItem,
  TextInput,
} from '@utilitywarehouse/hearth-react';
import { FormikProps, useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import { ChangeEvent, useEffect } from 'react';

import { useFieldProps } from '@hooks/useFieldProps';
import { useDetailsContext } from '@providers/basket/DetailsProvider/Context';
import { BooleanSelection } from '@src/types/index';
import { Title, YourDetailsFormValues } from '@src/types/yourDetails';
import { useTrackingContext } from '@tracking/Provider/Context';
import { useTrackSignupJointAccountHolderAddedOnce } from '@tracking/trackingEvents';

import { DoBField } from '../DoBField';
import '../styles.scss';
import { YourAccountFormErrors } from '../types';

interface YourAccountProps {
  hasCbc: boolean;
}

const personalTitles = Object.values(Title).map((title) => ({
  label: title,
  value: title,
}));

const getCbcValues = (values: YourDetailsFormValues) => {
  const {
    yaAdditionalAccountNameFirst,
    yaAdditionalAccountNameLast,
    yaAdditionalAccountDoB,
    yaAdditionalAccountContactNumber,
    yaAdditionalAccountEmail,
  } = values;

  const cbcValues: Partial<YourDetailsFormValues> = {};

  if (values.yaAdditionalAccountNameTitle) {
    cbcValues.yaSecondCbcNameTitle = values.yaAdditionalAccountNameTitle;
  }

  if (yaAdditionalAccountNameFirst) {
    cbcValues.yaSecondCbcNameFirst = yaAdditionalAccountNameFirst;
  }

  if (yaAdditionalAccountNameLast) {
    cbcValues.yaSecondCbcNameLast = yaAdditionalAccountNameLast;
  }

  if (yaAdditionalAccountDoB) {
    cbcValues.yaSecondCbcDoB = yaAdditionalAccountDoB;
  }

  if (yaAdditionalAccountContactNumber) {
    cbcValues.yaSecondCbcContactNumber = yaAdditionalAccountContactNumber;
  }

  if (yaAdditionalAccountEmail) {
    cbcValues.yaSecondCbcEmail = yaAdditionalAccountEmail;
  }

  if (isEmpty(cbcValues)) {
    return null;
  }

  return cbcValues;
};

const disabledPopulateCbcDetails = (errors: YourAccountFormErrors) => {
  const {
    yaAdditionalAccountNameTitle,
    yaAdditionalAccountNameFirst,
    yaAdditionalAccountNameLast,
    yaAdditionalAccountDoB,
    yaAdditionalAccountContactNumber,
    yaAdditionalAccountEmail,
  } = errors;

  return Boolean(
    yaAdditionalAccountNameTitle ||
    yaAdditionalAccountNameFirst ||
    yaAdditionalAccountNameLast ||
    yaAdditionalAccountDoB ||
    yaAdditionalAccountContactNumber ||
    yaAdditionalAccountEmail
  );
};

const YourAccount = ({ hasCbc }: YourAccountProps) => {
  const trackJointAccountHolderAdded =
    useTrackSignupJointAccountHolderAddedOnce();
  const { trackingContexts } = useTrackingContext();

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitCount,
    setFieldValue,
    setValues,
  }: FormikProps<YourDetailsFormValues> = useFormikContext();

  const { setDetailsItem } = useDetailsContext();

  const getFieldProps = useFieldProps<YourDetailsFormValues>({
    values,
    errors,
    handleChange: (event: ChangeEvent<HTMLInputElement>) => {
      handleChange(event);
      setDetailsItem(
        event.target.name as keyof YourDetailsFormValues,
        event.target.value
      );
    },
    handleBlur,
    touched,
    submitCount,
  });

  useEffect(() => {
    if (values.yaAdditionalAccount === BooleanSelection.Yes) {
      trackJointAccountHolderAdded({}, { contexts: trackingContexts });
    }
  }, [
    values.yaAdditionalAccount,
    trackJointAccountHolderAdded,
    trackingContexts,
  ]);

  const handlePopulateCbcDetails = (checked: boolean) => {
    if (!checked) {
      setFieldValue('yaSecondCbcUseDetails', BooleanSelection.No);
      return;
    }

    const cbcValues = getCbcValues(values);

    if (!cbcValues) {
      return;
    }

    setValues({
      ...values,
      ...cbcValues,
      yaSecondCbcUseDetails: BooleanSelection.Yes,
    });
  };

  const showJointAccountHolder =
    values.yaAdditionalAccount === BooleanSelection.Yes;

  const renderCbcSection = () => {
    if (!hasCbc) {
      return null;
    }

    return (
      <Flex spacing="xl" direction="column">
        <RadioGroup
          {...getFieldProps('yaSecondCbcAccount')}
          direction="row"
          label="Would you like to add another cardholder for your Cashback Card?"
          className="details__RadioGroup"
          onValueChange={(val) => setFieldValue('yaSecondCbcAccount', val)}
          helperText="Additional cardholders must be 18 years or older and live at your
          current address."
        >
          <RadioTile
            data-testid="yaSecondCbcAccountYes"
            value={BooleanSelection.Yes}
            label="Yes"
          />
          <RadioTile
            data-testid="yaSecondCbcAccountNo"
            value={BooleanSelection.No}
            label="No"
          />
        </RadioGroup>

        {values.yaSecondCbcAccount === BooleanSelection.Yes && (
          <>
            <Flex
              direction="column"
              spacing="xl"
              className="details__inputWrapper"
            >
              {values.yaAdditionalAccount === BooleanSelection.Yes && (
                <Checkbox
                  data-testid="yaSecondCbcUseDetails"
                  name="yaSecondCbcUseDetails"
                  checked={
                    values.yaSecondCbcUseDetails === BooleanSelection.Yes
                  }
                  onCheckedChange={handlePopulateCbcDetails}
                  label={<BodyText>Use joint account holder details</BodyText>}
                  disabled={disabledPopulateCbcDetails(errors)}
                />
              )}
              <Select
                {...getFieldProps('yaSecondCbcNameTitle')}
                className="details__formInput"
                label="Title"
                data-testid="yaSecondCbcNameTitle"
                onValueChange={(val) =>
                  setFieldValue('yaSecondCbcNameTitle', val)
                }
                required
              >
                {personalTitles.map(({ label, value }) => (
                  <SelectItem value={value} key={value}>
                    {label}
                  </SelectItem>
                ))}
              </Select>
              <TextInput
                {...getFieldProps('yaSecondCbcNameFirst')}
                className="details__formInput"
                label="First name"
                data-testid="yaSecondCbcNameFirst"
                required
              />
              <TextInput
                {...getFieldProps('yaSecondCbcNameLast')}
                className="details__formInput"
                label="Last name"
                data-testid="yaSecondCbcNameLast"
                required
              />
              <DoBField
                {...getFieldProps('yaSecondCbcDoB')}
                className="details__formInput"
                onChange={(value) => {
                  setDetailsItem('yaSecondCbcDoB', value);
                  setFieldValue('yaSecondCbcDoB', value);
                }}
                label="Date of birth"
                data-testid="yaSecondCbcDoB"
                required
              />
              <TextInput
                {...getFieldProps('yaSecondCbcContactNumber')}
                className="details__formInput"
                label="Mobile number"
                type="tel"
                data-testid="yaSecondCbcContactNumber"
                helperText="We’ll use this to text you a code to verify your identity when
                making online purchases or changes to your card settings."
                required
              />
            </Flex>
            {!values.yaSecondCbcContactNumber && (
              <Alert colorScheme="info">
                <BodyText>
                  We highly recommend entering the contact number of the
                  additional cardholder to make sure they have the best possible
                  experience.
                </BodyText>
              </Alert>
            )}
            <Flex
              direction="column"
              spacing="xl"
              className="details__inputWrapper"
            >
              <TextInput
                {...getFieldProps('yaSecondCbcEmail')}
                className="details__formInput"
                label="Email address"
                type="email"
                data-testid="yaSecondCbcEmail"
                required
              />
            </Flex>
          </>
        )}
      </Flex>
    );
  };

  return (
    <>
      <PasswordInput
        {...getFieldProps('yaPassword')}
        className="details__formInput"
        label="Choose your online account password"
        autoComplete="new-password"
        data-testid="yaPassword"
        helperText="At least 8 characters, 1 upper case letter, 1 number"
        required
      />
      <Flex spacing="xl" direction="column">
        <RadioGroup
          {...getFieldProps('yaAdditionalAccount')}
          label="Would you like to add a joint account holder?"
          className="details__RadioGroup"
          direction="row"
          onValueChange={(val) => setFieldValue('yaAdditionalAccount', val)}
        >
          <RadioTile
            data-testid="yaAdditionalAccountYes"
            value={BooleanSelection.Yes}
            label="Yes"
            id="yaAdditionalAccountYes"
          />
          <RadioTile
            data-testid="yaAdditionalAccountNo"
            value={BooleanSelection.No}
            label="No"
            id="yaAdditionalAccountNo"
          />
        </RadioGroup>

        {showJointAccountHolder && (
          <Flex
            spacing="xl"
            direction="column"
            className="details__inputWrapper"
          >
            <Select
              {...getFieldProps('yaAdditionalAccountNameTitle')}
              className="details__formInput"
              label="Title"
              data-testid="yaAdditionalAccountNameTitle"
              onValueChange={(val) =>
                setFieldValue('yaAdditionalAccountNameTitle', val)
              }
              required
            >
              {personalTitles.map(({ label, value }) => (
                <SelectItem value={value} key={value}>
                  {label}
                </SelectItem>
              ))}
            </Select>
            <TextInput
              {...getFieldProps('yaAdditionalAccountNameFirst')}
              className="details__formInput"
              label="First name"
              data-testid="yaAdditionalAccountNameFirst"
              required
            />
            <TextInput
              {...getFieldProps('yaAdditionalAccountNameLast')}
              className="details__formInput"
              label="Last name"
              data-testid="yaAdditionalAccountNameLast"
              required
            />
            <DoBField
              {...getFieldProps('yaAdditionalAccountDoB')}
              className="details__formInput"
              onChange={(value) => {
                setDetailsItem('yaAdditionalAccountDoB', value);
                setFieldValue('yaAdditionalAccountDoB', value);
              }}
              label="Date of birth"
              data-testid="yaAdditionalAccountDoB"
              required
            />
            <TextInput
              {...getFieldProps('yaAdditionalAccountContactNumber')}
              className="details__formInput"
              type="tel"
              label="Contact number"
              data-testid="yaAdditionalAccountContactNumber"
              required
            />
            <TextInput
              {...getFieldProps('yaAdditionalAccountEmail')}
              className="details__formInput"
              type="email"
              label="Email address"
              data-testid="yaAdditionalAccountEmail"
              required
            />
          </Flex>
        )}
      </Flex>

      {renderCbcSection()}
    </>
  );
};

export { YourAccount };
